import React, { useMemo, useRef, useEffect } from "react";
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  ServerError
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import { useHistory } from "react-router-dom";
import getStore from "store/zustandStore";
import authService from "services/authService";
import { useSnackbar } from "notistack";

const authMiddleware = new ApolloLink((operation, forward) => {
  const operationName = operation.operationName;
  
  // Check if this is a public operation
  if (authService.isPublicOperation(operationName)) {
    // Explicitly remove auth headers for public operations
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: undefined,
        Authorization: undefined
      }
    }));
  } else {
    // For protected operations, only add valid token
    const token = authService.getAuthToken();
    if (token && authService.isTokenValid()) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `JWT ${token}`,
          Authorization: `JWT ${token}`
        }
      }));
    } else if (token && !authService.isTokenValid()) {
      // Token exists but is invalid, clear it
      authService.clearAuthToken();
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: undefined,
          Authorization: undefined
        }
      }));
    }
  }

  return forward(operation);
});

const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_HTTP_URI
}) as unknown as ApolloLink;

const createErrorLink = (history, setLoading, enqueueSnackbar) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    const operationName = operation.operationName;
    
    // Handle network errors (including 500s)
    if (networkError) {
      const statusCode = (networkError as any).statusCode;

      // Handle 500 errors - especially for login operations
      if (statusCode === 500) {
        // For login operations, clear everything and retry
        if (operationName && operationName.toLowerCase().includes("login")) {
          if (authService.shouldRetry()) {
            // Clear all site data
            authService.clearAllSiteData().then(() => {
              // Remove auth header
              operation.setContext({
                headers: {
                  ...operation.getContext().headers,
                  authorization: undefined,
                  Authorization: undefined
                }
              });
            });

            // Show message to user
            enqueueSnackbar?.("Authentication error. Retrying...", {
              variant: "info"
            });

            // Retry the operation
            return forward(operation);
          }
        }

        // For other operations, check if it's auth related
        const errorMessage = networkError.message || "";
        if (errorMessage.includes("expired") || errorMessage.includes("authentication")) {
          authService.handleAuthError(networkError);
          enqueueSnackbar?.("Your session has expired. Please login again.", {
            variant: "warning"
          });
          return;
        }
      }

      // Handle 401 Unauthorized
      if (statusCode === 401) {
        authService.handleAuthError(networkError);
        enqueueSnackbar?.("Authentication required. Please login again.", {
          variant: "warning"
        });
        return;
      }

      // Check for network connectivity issues
      if (!window.navigator.onLine) {
        console.error(`[Network error]: No internet connection.`);
        enqueueSnackbar?.("No internet connection.", {
          variant: "error"
        });
        setLoading(false);
        return;
      }

      console.error(
        `[Server error]: A server error (${networkError}) occurred.`
      );
    }

    // Handle GraphQL errors
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        // Check for Rails backend errors
        if (err.message?.includes("undefined method") || 
            err.message?.includes("should_refresh_token")) {
          console.error("[Rails API Error]:", err.message);
          
          // For login operations, retry without token
          if (operationName && operationName.toLowerCase().includes("login")) {
            operation.setContext({
              headers: {
                ...operation.getContext().headers,
                authorization: undefined,
                Authorization: undefined
              }
            });
            
            // Don't show error for backend issues during migration
            return forward(operation);
          }
          
          enqueueSnackbar?.("Backend service is being updated. Please try again.", {
            variant: "info"
          });
          return;
        }
        
        // Check for authentication errors
        if (authService.handleAuthError(err)) {
          enqueueSnackbar?.("Session expired. Please login again.", {
            variant: "warning"
          });
          return;
        }

        // For public operations with auth errors, retry without token
        if (operationName && authService.isPublicOperation(operationName)) {
          if (
            err.message?.toLowerCase().includes("expired") ||
            err.message?.toLowerCase().includes("authentication") ||
            err.message?.toLowerCase().includes("jwt")
          ) {
            // Remove auth header and retry
            operation.setContext({
              headers: {
                ...operation.getContext().headers,
                authorization: undefined,
                Authorization: undefined
              }
            });

            return forward(operation);
          }
        }

        console.error(
          `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`
        );
      }
    }
  });

const AppApolloProvider = ({ children }) => {
  const history = useHistory();
  const setLoading = getStore((state: any) => state.setLoading);
  const { enqueueSnackbar } = useSnackbar();
  const clientRef = useRef<ApolloClient<any> | null>(null);

  // Memoize the Apollo Client instance to ensure it is only created once
  const client = useMemo(() => {
    const errorLink = createErrorLink(history, setLoading, enqueueSnackbar);

    const apolloClient = new ApolloClient({
      link: ApolloLink.from([authMiddleware, errorLink, uploadLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "network-only",
          errorPolicy: "all"
        },
        query: {
          fetchPolicy: "network-only",
          errorPolicy: "all"
        },
        mutate: {
          errorPolicy: "all"
        }
      }
    });

    // Store reference globally for auth service to use
    (window as any).__APOLLO_CLIENT__ = apolloClient;
    clientRef.current = apolloClient;

    return apolloClient;
  }, [history, enqueueSnackbar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if ((window as any).__APOLLO_CLIENT__) {
        delete (window as any).__APOLLO_CLIENT__;
      }
    };
  }, []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default AppApolloProvider;
